import { useState, useEffect, useCallback } from "react";

interface TokenUsage {
  input_tokens: number;
  output_tokens: number;
  model: string;
  provider: string;
  timestamp: string;
}

interface TaskCostEntry {
  task_id: string;
  prompt_preview: string;
  usage: TokenUsage;
  estimated_cost_usd: number;
}

interface CostSummary {
  total_cost_usd: number;
  task_count: number;
  avg_cost_per_task: number;
  total_input_tokens: number;
  total_output_tokens: number;
  budget_usd?: number;
  session_active?: boolean;
  by_provider: Record<string, number>;
  by_model: Record<string, number>;
}

/* ── Design tokens ─────────────────────────────────────────── */
const ACCENT = "#50fa7b";
const TEXT = "#f8f8f2";
const TEXT_DIM = "#6272a4";
const CARD_BORDER = "#1a2330";
const MONO = "'JetBrains Mono', monospace";

const PROVIDER_COLORS: Record<string, string> = {
  ollama:    "#8be9fd",  // blue
  openai:    "#50fa7b",  // green
  anthropic: "#ffb86c",  // orange
  google:    "#f1fa8c",  // yellow
  mistral:   "#bd93f9",  // violet
  cohere:    "#ff79c6",  // pink
};
const FALLBACK_PROVIDER_COLORS = ["#bd93f9", "#bd93f9", "#8be9fd", "#ff5555", "#f1fa8c", "#8be9fd"];

function providerColor(name: string): string {
  const key = name.toLowerCase();
  return PROVIDER_COLORS[key] ?? FALLBACK_PROVIDER_COLORS[
    [...key].reduce((h, c) => h + c.charCodeAt(0), 0) % FALLBACK_PROVIDER_COLORS.length
  ];
}

const BAR_COLORS = ["#50fa7b", "#6be0c8", "#8be9fd", "#bd93f9", "#bd93f9", "#ff5555"];

/* ── Helpers ───────────────────────────────────────────────── */
function formatCost(usd: number): string {
  if (usd === 0) return "$0.00";
  if (usd < 0.001) return `$${usd.toFixed(6)}`;
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(4)}`;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

function timeAgo(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}

/* ── Inline keyframes (injected once) ─────────────────────── */
const STYLE_ID = "cost-ledger-keyframes";
function ensureKeyframes() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes costPulse {
      0%, 100% { opacity: 1; box-shadow: 0 0 4px rgba(80,250,123,0.5); }
      50%      { opacity: .55; box-shadow: 0 0 8px rgba(80,250,123,0.8); }
    }
    @keyframes budgetBarGrow {
      from { width: 0%; }
    }
    @keyframes shimmerIn {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `;
  document.head.appendChild(style);
}

/* ── Glassmorphism base ────────────────────────────────────── */
const glass = (accentHex: string, intensity = 0.07): React.CSSProperties => ({
  background: `linear-gradient(135deg, rgba(12,16,20,0.82), rgba(12,16,20,0.60))`,
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: `1px solid rgba(255,255,255,0.06)`,
  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), 0 0 40px ${accentHex}${Math.round(intensity * 255).toString(16).padStart(2, "0")}`,
  borderRadius: "10px",
});

/* ── Main component ────────────────────────────────────────── */
export function CostLedgerWorkbench() {
  const [summary, setSummary] = useState<CostSummary | null>(null);
  const [entries, setEntries] = useState<TaskCostEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => ensureKeyframes(), []);

  const fetchData = useCallback(() => {
    Promise.all([
      fetch("/api/cost/summary").then((r) => r.json()),
      fetch("/api/cost/ledger").then((r) => r.json()),
    ])
      .then(([summaryData, ledgerData]) => {
        setSummary(summaryData);
        setEntries(ledgerData.entries || []);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const onUsage = () => fetchData();
    window.addEventListener("opentool-agent-usage", onUsage);
    return () => window.removeEventListener("opentool-agent-usage", onUsage);
  }, [fetchData]);

  /* ── Loading / Error states ──────────────────────────────── */
  if (loading) {
    return (
      <div className="slab-grid slab-11" style={{ height: "calc(100vh - 100px)" }}>
        <div className="block" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: TEXT_DIM, fontSize: ".85rem", fontFamily: MONO }}>Loading cost data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="slab-grid slab-11" style={{ height: "calc(100vh - 100px)" }}>
        <div className="block" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: "#ff5555", fontSize: ".85rem" }}>Error: {error}</span>
        </div>
      </div>
    );
  }

  const providerEntries = Object.entries(summary?.by_provider || {}).sort((a, b) => b[1] - a[1]);
  const modelEntries = Object.entries(summary?.by_model || {}).sort((a, b) => b[1] - a[1]);
  const maxModelCost = Math.max(...modelEntries.map(([, v]) => v), 0.0001);

  const budgetUsd = summary?.budget_usd || 0;
  const budgetPct = budgetUsd > 0
    ? Math.min(((summary?.total_cost_usd || 0) / budgetUsd) * 100, 100)
    : 0;

  return (
    <div style={{ height: "calc(100vh - 100px)", overflow: "auto", padding: "0 .25rem" }}>

      {/* ── Session status bar ───────────────────────────────── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: ".5rem",
        padding: ".25rem 0",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".45rem" }}>
          <div style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: summary?.session_active !== false ? ACCENT : TEXT_DIM,
            animation: summary?.session_active !== false ? "costPulse 2s ease-in-out infinite" : "none",
            flexShrink: 0,
          }} />
          <span style={{
            fontSize: ".7rem",
            color: summary?.session_active !== false ? ACCENT : TEXT_DIM,
            fontFamily: MONO,
            fontWeight: 500,
            letterSpacing: ".03em",
          }}>
            {summary?.session_active !== false ? "Session Active" : "Session Idle"}
          </span>
        </div>
        <span style={{ fontSize: ".65rem", color: TEXT_DIM, fontFamily: MONO }}>
          {new Date().toLocaleTimeString()}
        </span>
      </div>

      {/* ── Summary cards ────────────────────────────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: ".5rem",
        marginBottom: ".75rem",
      }}>
        <SummaryCard label="Total Spend" value={formatCost(summary?.total_cost_usd || 0)} accent={ACCENT} delay={0} />
        <SummaryCard label="Tasks" value={String(summary?.task_count || 0)} accent="#8be9fd" delay={1} />
        <SummaryCard label="Avg / Task" value={formatCost(summary?.avg_cost_per_task || 0)} accent="#bd93f9" delay={2} />
        <SummaryCard
          label="Tokens"
          value={`${formatTokens(summary?.total_input_tokens || 0)} in / ${formatTokens(summary?.total_output_tokens || 0)} out`}
          accent="#bd93f9"
          delay={3}
        />
      </div>

      {/* ── Budget progress bar ──────────────────────────────── */}
      {budgetUsd > 0 && (
        <div style={{
          ...glass("#50fa7b", 0.04),
          padding: ".55rem .75rem",
          marginBottom: ".75rem",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: ".3rem" }}>
            <span style={{ fontSize: ".65rem", color: TEXT_DIM, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>
              Budget Usage
            </span>
            <span style={{ fontSize: ".7rem", fontFamily: MONO, fontWeight: 600, color: budgetBarColor(budgetPct) }}>
              {formatCost(summary?.total_cost_usd || 0)} / {formatCost(budgetUsd)} ({budgetPct.toFixed(1)}%)
            </span>
          </div>
          <BudgetBar pct={budgetPct} />
        </div>
      )}

      {/* ── Provider & Model breakdown ───────────────────────── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: ".5rem",
        marginBottom: ".75rem",
      }}>
        {/* Provider breakdown */}
        <div style={{ ...glass("#50fa7b", 0.04), padding: ".75rem" }}>
          <h4 style={{ color: TEXT, fontSize: ".75rem", marginBottom: ".6rem", fontWeight: 600 }}>
            Cost by Provider
          </h4>
          {providerEntries.length === 0 ? (
            <div style={{ color: TEXT_DIM, fontSize: ".75rem" }}>No data yet</div>
          ) : (
            providerEntries.map(([name, cost]) => {
              const color = providerColor(name);
              const total = summary?.total_cost_usd || 1;
              return (
                <ProviderRow
                  key={name}
                  name={name}
                  cost={cost}
                  pct={(cost / total) * 100}
                  color={color}
                />
              );
            })
          )}
        </div>

        {/* Model breakdown */}
        <div style={{ ...glass("#bd93f9", 0.04), padding: ".75rem" }}>
          <h4 style={{ color: TEXT, fontSize: ".75rem", marginBottom: ".6rem", fontWeight: 600 }}>
            Cost by Model
          </h4>
          {modelEntries.length === 0 ? (
            <div style={{ color: TEXT_DIM, fontSize: ".75rem" }}>No data yet</div>
          ) : (
            modelEntries.map(([name, cost], i) => (
              <BarRow
                key={name}
                label={name}
                value={formatCost(cost)}
                pct={(cost / maxModelCost) * 100}
                color={BAR_COLORS[i % BAR_COLORS.length]}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Ledger table ─────────────────────────────────────── */}
      <div style={{ ...glass("#8be9fd", 0.03), padding: ".75rem" }}>
        <h4 style={{ color: TEXT, fontSize: ".75rem", marginBottom: ".5rem", fontWeight: 600 }}>
          Recent Tasks
          {entries.length > 0 && (
            <span style={{ fontWeight: 400, color: TEXT_DIM, marginLeft: ".5rem" }}>
              ({entries.length})
            </span>
          )}
        </h4>
        {entries.length === 0 ? (
          <div style={{ color: TEXT_DIM, fontSize: ".75rem", padding: ".5rem 0" }}>
            No tasks recorded yet. Costs will appear here as you use the agent.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* Header */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "80px 1fr 110px 100px 80px 70px",
              gap: ".5rem",
              padding: ".35rem .5rem",
              fontSize: ".65rem",
              color: TEXT_DIM,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: ".04em",
              borderBottom: `1px solid rgba(255,255,255,0.06)`,
              fontFamily: MONO,
            }}>
              <span>Task</span>
              <span>Prompt</span>
              <span>Provider</span>
              <span>Tokens</span>
              <span>Cost</span>
              <span>When</span>
            </div>
            {entries.map((entry, idx) => (
              <div
                key={entry.task_id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "80px 1fr 110px 100px 80px 70px",
                  gap: ".5rem",
                  padding: ".35rem .5rem",
                  fontSize: ".72rem",
                  color: TEXT,
                  background: idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)",
                  borderBottom: `1px solid rgba(255,255,255,0.025)`,
                  alignItems: "center",
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = "rgba(80,250,123,0.04)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)"; }}
              >
                <span style={{ fontFamily: MONO, color: TEXT_DIM, fontSize: ".65rem" }}>
                  {entry.task_id}
                </span>
                <span style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {entry.prompt_preview}
                </span>
                <span style={{ display: "flex", alignItems: "center", gap: ".35rem", fontSize: ".68rem" }}>
                  <span style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: providerColor(entry.usage.provider),
                    display: "inline-block",
                    flexShrink: 0,
                  }} />
                  <span style={{ color: providerColor(entry.usage.provider), fontFamily: MONO, fontWeight: 500 }}>
                    {entry.usage.provider}
                  </span>
                </span>
                <span style={{ fontFamily: MONO, fontSize: ".68rem", color: TEXT_DIM }}>
                  {formatTokens(entry.usage.input_tokens)}/{formatTokens(entry.usage.output_tokens)}
                </span>
                <span style={{
                  fontFamily: MONO,
                  fontWeight: 600,
                  color: entry.estimated_cost_usd > 0 ? ACCENT : TEXT_DIM,
                }}>
                  {formatCost(entry.estimated_cost_usd)}
                </span>
                <span style={{ fontSize: ".65rem", color: TEXT_DIM, fontFamily: MONO }}>
                  {timeAgo(entry.usage.timestamp)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────── */

function SummaryCard({ label, value, accent, delay }: { label: string; value: string; accent: string; delay: number }) {
  return (
    <div style={{
      ...glass(accent, 0.09),
      padding: ".65rem .8rem",
      display: "flex",
      flexDirection: "column",
      gap: ".2rem",
      animation: `shimmerIn 0.4s ease ${delay * 0.08}s both`,
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Subtle top-edge glow line */}
      <div style={{
        position: "absolute",
        top: 0,
        left: "10%",
        right: "10%",
        height: "1px",
        background: `linear-gradient(90deg, transparent, ${accent}44, transparent)`,
      }} />
      <span style={{
        fontSize: ".62rem",
        color: TEXT_DIM,
        textTransform: "uppercase",
        letterSpacing: ".05em",
        fontWeight: 600,
      }}>
        {label}
      </span>
      <span style={{
        fontSize: "1.15rem",
        fontWeight: 700,
        color: accent,
        fontFamily: MONO,
        letterSpacing: "-.01em",
      }}>
        {value}
      </span>
    </div>
  );
}

function budgetBarColor(pct: number): string {
  if (pct >= 95) return "#ff5555";   // red
  if (pct >= 80) return "#f1fa8c";   // yellow
  return ACCENT;                      // mint
}

function BudgetBar({ pct }: { pct: number }) {
  const fill = budgetBarColor(pct);
  return (
    <div style={{
      height: 6,
      borderRadius: 3,
      background: "rgba(255,255,255,0.04)",
      overflow: "hidden",
      position: "relative",
    }}>
      <div style={{
        height: "100%",
        width: `${Math.max(pct, 1)}%`,
        borderRadius: 3,
        background: `linear-gradient(90deg, ${fill}CC, ${fill})`,
        boxShadow: `0 0 8px ${fill}66`,
        animation: "budgetBarGrow 0.8s ease-out",
        transition: "width 0.5s ease, background 0.5s ease",
      }} />
    </div>
  );
}

function ProviderRow({ name, cost, pct, color }: { name: string; cost: number; pct: number; color: string }) {
  return (
    <div style={{ marginBottom: ".4rem" }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontSize: ".7rem",
        marginBottom: ".15rem",
      }}>
        <span style={{ display: "flex", alignItems: "center", gap: ".35rem", color: TEXT }}>
          <span style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: color,
            display: "inline-block",
            boxShadow: `0 0 6px ${color}55`,
            flexShrink: 0,
          }} />
          {name}
        </span>
        <span style={{ fontFamily: MONO, color, fontWeight: 600 }}>
          {formatCost(cost)}
          <span style={{ color: TEXT_DIM, fontWeight: 400, marginLeft: ".35rem" }}>
            ({pct.toFixed(1)}%)
          </span>
        </span>
      </div>
      <div style={{
        height: 4,
        borderRadius: 2,
        background: `${color}14`,
        overflow: "hidden",
      }}>
        <div style={{
          height: "100%",
          width: `${Math.max(pct, 2)}%`,
          background: `linear-gradient(90deg, ${color}AA, ${color})`,
          borderRadius: 2,
          transition: "width 0.4s ease",
        }} />
      </div>
    </div>
  );
}

function BarRow({ label, value, pct, color }: { label: string; value: string; pct: number; color: string }) {
  return (
    <div style={{ marginBottom: ".35rem" }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: ".7rem",
        marginBottom: ".15rem",
      }}>
        <span style={{ color: TEXT }}>{label}</span>
        <span style={{ fontFamily: MONO, color, fontWeight: 600 }}>{value}</span>
      </div>
      <div style={{
        height: 4,
        borderRadius: 2,
        background: `${color}14`,
        overflow: "hidden",
      }}>
        <div style={{
          height: "100%",
          width: `${Math.max(pct, 2)}%`,
          background: `linear-gradient(90deg, ${color}AA, ${color})`,
          borderRadius: 2,
          transition: "width 0.3s ease",
        }} />
      </div>
    </div>
  );
}
